import React, { useState } from "react";
import toast from "react-hot-toast";
import {
    Stack,
    Grid,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';

import {
    Select,
    Input,
    SecondaryButton,
    InputMobileNumber,
    InputEmail
} from '../../Common';

import {
    trakrefFontFamilySemiBold,
    belgiumStates,
    frenchStates,
    ukStates,
    uaeEmirates,
} from '../../../utils';
import { Grey3 } from "../../../color_utils";

const Agents = ({
    initialState,
    agents,
    setAgents,
    countries,
    states,
}) => {

    const [statesOption, setStatesOption] = useState([]);

    const onExpand = (currentIndex, isExpanded) => {
        if (currentIndex <= -1)
            return;

        const newAgents = agents.map((agent, index) => ({
            ...agent,
            isOpen: currentIndex === index ? isExpanded : false
        }));

        setAgents(newAgents);
    }

    const onAdd = () => {
        const newAgents = agents.map(agent => ({
            ...agent,
            nameHasError: agent.name ? false : true,
            isOpen: !agent.name || agent.emailHasError || agent.phoneHasError ? true : false
        }));

        if (newAgents.filter(x => x.nameHasError || x.emailHasError || x.phoneHasError).length > 0) {
            setAgents(newAgents);
            return;
        }

        setAgents([
            ...newAgents,
            {
                id: crypto.randomUUID(),
                ...initialState
            }
        ]);
    }

    const onDelete = (e, id) => {
        e.stopPropagation();

        if (agents && agents.length === 1)
            return;

        const newAgents = agents.filter(agent => agent.id !== id);
        setAgents(newAgents);

        toast.success('The agent has been successfully deleted.', { duration: 6000 });
    }

    const onChange = (currentIndex, name, value) => {

        if (name === "country") {
            if (value === "BE") {
                setStatesOption(belgiumStates)
            }
            else if (value === "FR") {
                setStatesOption(frenchStates)
            }
            else if (value === "GB") {
                setStatesOption(ukStates)
            }
            else if (value === "UAE") {
                setStatesOption(uaeEmirates)
            }
            else {
                const filteredStates = states.filter(x => x.countryCode === value);
                setStatesOption(filteredStates);
            }
        }

        const newAgents = agents.map((agent, index) => {
            if (currentIndex === index) {

                const resetState = name === "country" ? { state: "" } : {};

                return {
                    ...agent,
                    ...resetState,
                    [name]: value,
                    nameHasError: name === "name" ? !value : agent.nameHasError,
                }
            }

            return agent;
        });

        setAgents(newAgents);
    }

    return (
        <Grid container spacing={2} marginTop={"25px"}>
            {
                agents && agents.map((agent, index) => (
                    <Grid key={agent.id} item xs={12}>
                        <Accordion
                            variant='outlined'
                            expanded={agent.isOpen}
                            onChange={(e, isExpanded) => onExpand(index, isExpanded)}
                            TransitionProps={{ unmountOnExit: true }}
                            sx={{ marginBottom: '1%' }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                            >
                                <Grid container direction='row'>
                                    <Grid item sx={10}>
                                        <Typography
                                            sx={{
                                                flexShrink: 0,
                                                color: Grey3,
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                fontFamily: trakrefFontFamilySemiBold,
                                                lineHeight: 2.5
                                            }}
                                        >
                                            Agent # {index + 1}
                                        </Typography>
                                    </Grid>
                                    <Grid item sx={{ marginLeft: 'auto' }}>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                onClick={(e) => onDelete(e, agent.id)}
                                                disabled={agents.length === 1}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={5}>
                                    <Grid item xs={6} lg={6}>
                                        <Stack spacing={"25px"}>
                                            <Input
                                                required
                                                fullWidth
                                                label="Name"
                                                value={agent.name}
                                                error={agent.nameHasError}
                                                onChange={(e) => onChange(index, "name", e.target.value)}
                                            />
                                            <Input
                                                fullWidth
                                                label="Agency"
                                                value={agent.agent}
                                                onChange={(e) => onChange(index, "agent", e.target.value)}
                                            />
                                            <Input
                                                fullWidth
                                                label="Badge number"
                                                value={agent.badgeNumber}
                                                onChange={(e) => onChange(index, "badgeNumber", e.target.value)}
                                            />
                                            <InputMobileNumber
                                                fullWidth
                                                label="Contact number"
                                                value={agent.contactNumber}
                                                onChange={(e) => onChange(index, "contactNumber", e.target.value)}
                                                isValid={(error) => onChange(index, "phoneHasError", error)}
                                                getCountryCode={(value) =>
                                                    onChange(index, "countryCode", value)
                                                }
                                            />
                                            <Input
                                                fullWidth
                                                label="Address"
                                                value={agent.address}
                                                onChange={(e) => onChange(index, "address", e.target.value)}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={6} lg={6}>
                                        <Stack spacing={"25px"}>
                                            <Input
                                                fullWidth
                                                label="City"
                                                value={agent.city}
                                                onChange={(e) => onChange(index, "city", e.target.value)}
                                            />
                                            <Select
                                                fullWidth
                                                label="Country"
                                                value={agent.country}
                                                options={countries}
                                                onChange={(e) => onChange(index, "country", e.target.value)}
                                            />
                                            {
                                                statesOption.length ? (
                                                    <Select
                                                        fullWidth
                                                        label="State"
                                                        value={agent.state}
                                                        options={statesOption}
                                                        onChange={(e) => onChange(index, "state", e.target.value)}
                                                    />
                                                ) : (
                                                    <Input
                                                        fullWidth
                                                        label="State"
                                                        value={agent.state}
                                                        onChange={(e) => onChange(index, "state", e.target.value)}
                                                    />
                                                )
                                            }

                                            <InputEmail
                                                fullWidth
                                                label="Email Address"
                                                value={agent.emailAddress}
                                                onChange={(e) => onChange(index, "emailAddress", e.target.value)}
                                                isValid={(error) => onChange(index, "emailHasError", error)}
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                ))
            }

            <Grid item xs={12}>
                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    flexDirection: "row",
                    marginTop: "10px"
                }}>
                    <SecondaryButton
                        useSmallButton={true}
                        isTextVariant={true}
                        buttonText='Add New Agent'
                        onClick={onAdd}
                        startIcon={<AddIcon />}
                    />
                </div>
            </Grid>
        </Grid>
    )
}

export default Agents;